import { useState, useEffect, useMemo } from "react";
import { Table, Icon, Progress } from "semantic-ui-react";
import { DateTime } from "luxon";
import { useInterval } from "usehooks-ts";

import { useRestClientContext } from "../../../contexts/RestClientContext";
import Team from "../../../rest/Team";
import { Export, JobStatus } from "../../../types/types";

import Suspense from "../Suspense/Suspense";
import Right from "../Right/Right";
import Button from "../Button/Button";

import StatusChip from "../StatusChip/StatusChip";

import "./ExportsTable.scss";
import ConfirmationModal from "../../Modal/ConfirmationModal";

interface ExportsTableProps {
	team: Team;
}

const activeStatuses: JobStatus[] = [JobStatus.PENDING, JobStatus.RUNNING];

export default function ExportsTable({ team }: ExportsTableProps) {
	const client = useRestClientContext();
	const [promise, setPromise] = useState<Promise<void>>();
	const [exports, setExports] = useState<Export[]>([]);
	const [reloads, setReloads] = useState(0);
	const [cancellingExport, setCancellingExport] = useState<Export>();
	const [pollDelay, setPollDelay] = useState(2000);

	const hasActiveExports = useMemo(() => exports.some((exprt) => activeStatuses.includes(exprt.status)), [exports]);

	useEffect(() => {
		setPromise(
			client.v3.fetchExports(team).then((json) => {
				setExports(json);
			})
		);
	}, [team, reloads, client]);

	useInterval(
		() => {
			client.v3.fetchExports(team).then(setExports);
			if (pollDelay < 8000) setPollDelay(pollDelay * 2);
		},
		hasActiveExports ? pollDelay : null
	);

	const formatDateTime = (dateTime: string) => {
		try {
			return DateTime.fromISO(dateTime).toLocaleString(DateTime.DATETIME_FULL);
		} catch (e) {}
	};

	const onReload = () => {
		setPollDelay(2000);
		setReloads(reloads + 1);
	};

	const onDownload = (exportRecord: Export) => {
		if (exportRecord.download_url) {
			window.open(exportRecord.download_url, "_blank");
		} else {
			client.v3.downloadExport(team, exportRecord.id);
		}
	};

	const onCancelConfirm = () => {
		if (!cancellingExport) return;

		setPromise(
			client.v3.deleteExport(team, cancellingExport.id).then(() => {
				setCancellingExport(undefined);
			})
		);
	};

	const onCancelExportAction = (exportRecord: Export) => setCancellingExport(exportRecord);

	return (
		<Suspense promise={promise}>
			<Right>
				<Button variant="text" onClick={onReload}>
					<Icon name="refresh"></Icon>
				</Button>
			</Right>
			<p>Please note, exports will get automatically deleted after a month.</p>
			<Table className="exports-table">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>ID</Table.HeaderCell>
						<Table.HeaderCell>Status</Table.HeaderCell>
						<Table.HeaderCell>Progress</Table.HeaderCell>
						<Table.HeaderCell>Filename</Table.HeaderCell>
						<Table.HeaderCell>Created at</Table.HeaderCell>
						<Table.HeaderCell>
							<span hidden>Actions</span>
						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{exports.map((exportRecord) => (
						<Table.Row key={exportRecord.id}>
							<Table.Cell>{exportRecord.id}</Table.Cell>
							<Table.Cell>
								<StatusChip status={exportRecord.status} />
							</Table.Cell>
							<Table.Cell className="progress">
								<Progress className={exportRecord.status} percent={exportRecord.progress} />
							</Table.Cell>
							<Table.Cell>{exportRecord.filename ?? "—"}</Table.Cell>
							<Table.Cell>{formatDateTime(exportRecord.created_at)}</Table.Cell>
							<Table.Cell>
								{exportRecord.status === "finished" && (
									<Button variant="contained" onClick={() => onDownload(exportRecord)}>
										Download
									</Button>
								)}
								{activeStatuses.includes(exportRecord.status) && (
									<Button variant="contained" onClick={() => onCancelExportAction(exportRecord)}>
										Cancel
									</Button>
								)}
							</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>
			</Table>
			{cancellingExport && (
				<ConfirmationModal
					title={"Confirm cancellation"}
					open={!!cancellingExport}
					buttonsDisabled={false}
					onConfirm={onCancelConfirm}
					onCancel={onReload}
				/>
			)}
		</Suspense>
	);
}
